import { AbstractModel } from '@/models/abstractModel';
import { IEventStatsFromWS } from '@/types/event';
import { listSiteDataTooltip } from '@/helpers';
import { useEventStore } from '@/stores/event';

export class EventStatsItem extends AbstractModel {
    public score: string | null;
    public scoreHome: number | null;
    public scoreAway: number | null;
    public scoreList: [number, string][] | null;
    public isScoresMismatch: 0 | 1 | null;
    public time: string | null;
    public timeList: [number, string, number, number][] | null;
    public redHome: number | null;
    public redAway: number | null;
    public yellowHome: number | null;
    public yellowAway: number | null;
    public foulsHome: number | null;
    public foulsAway: number | null;
    public possessionTimeHome: number | null;
    public possessionTimeAway: number | null;
    public attacksHome: number | null;
    public attacksAway: number | null;
    public dangerousAttacksHome: number | null;
    public dangerousAttacksAway: number | null;
    public shotsHome: number | null;
    public shotsAway: number | null;
    public shotsOnTargetHome: number | null;
    public shotsOnTargetAway: number | null;
    public hitWoodworkHome: number | null;
    public hitWoodworkAway: number | null;
    public cornersHome: number | null;
    public cornersAway: number | null;

    constructor(data: IEventStatsFromWS) {
        super();

        this.score = data.score;

        if (data.score !== null) {
            const score = data.score.split('-');
            this.scoreHome = Number(score[0]);
            this.scoreAway = Number(score[1]);
        } else {
            this.scoreHome = null;
            this.scoreAway = null;
        }

        this.scoreList = data.scores ? JSON.parse(data.scores) : null;
        this.isScoresMismatch = data.is_scores_mismatch;
        this.time = data.time;
        this.timeList = data.times ? JSON.parse(data.times) : null;
        this.redHome = data.red_home;
        this.redAway = data.red_away;
        this.yellowHome = data.yellow_home;
        this.yellowAway = data.yellow_away;
        this.foulsHome = data.fouls_home;
        this.foulsAway = data.fouls_away;
        this.possessionTimeHome = data.possession_time_home;
        this.possessionTimeAway = data.possession_time_away;
        this.attacksHome = data.attacks_home;
        this.attacksAway = data.attacks_away;
        this.dangerousAttacksHome = data.dangerous_attacks_home;
        this.dangerousAttacksAway = data.dangerous_attacks_away;
        this.shotsHome = data.shots_home;
        this.shotsAway = data.shots_away;
        this.shotsOnTargetHome = data.shots_on_target_home;
        this.shotsOnTargetAway = data.shots_on_target_away;
        this.hitWoodworkHome = data.hit_woodwork_home;
        this.hitWoodworkAway = data.hit_woodwork_away;
        this.cornersHome = data.corners_home;
        this.cornersAway = data.corners_away;
    }

    public update(data: IEventStatsFromWS) {
        this.score = data.score;

        if (data.score !== null) {
            const score = data.score.split('-');
            this.scoreHome = Number(score[0]);
            this.scoreAway = Number(score[1]);
        } else {
            this.scoreHome = null;
            this.scoreAway = null;
        }

        this.scoreList = data.scores ? JSON.parse(data.scores) : null;
        this.isScoresMismatch = data.is_scores_mismatch;
        this.time = data.time;
        this.timeList = data.times ? JSON.parse(data.times) : null;
        this.redHome = data.red_home;
        this.redAway = data.red_away;
        this.yellowHome = data.yellow_home;
        this.yellowAway = data.yellow_away;
        this.foulsHome = data.fouls_home;
        this.foulsAway = data.fouls_away;
        this.possessionTimeHome = data.possession_time_home;
        this.possessionTimeAway = data.possession_time_away;
        this.attacksHome = data.attacks_home;
        this.attacksAway = data.attacks_away;
        this.dangerousAttacksHome = data.dangerous_attacks_home;
        this.dangerousAttacksAway = data.dangerous_attacks_away;
        this.shotsHome = data.shots_home;
        this.shotsAway = data.shots_away;
        this.shotsOnTargetHome = data.shots_on_target_home;
        this.shotsOnTargetAway = data.shots_on_target_away;
        this.hitWoodworkHome = data.hit_woodwork_home;
        this.hitWoodworkAway = data.hit_woodwork_away;
        this.cornersHome = data.corners_home;
        this.cornersAway = data.corners_away;
    }

    public isStatsExist(): boolean {
        return (
            this.yellowHome !== null ||
            this.foulsHome !== null ||
            this.possessionTimeHome !== null ||
            this.attacksHome !== null ||
            this.dangerousAttacksHome !== null ||
            this.shotsHome !== null ||
            this.shotsOnTargetHome !== null ||
            this.hitWoodworkHome !== null ||
            this.cornersHome !== null
        );
    }

    get displayedTime(): string {
        if (!this.time) {
            return this.t('event.unavailable');
        }
        let time = this.time;
        if (time === 'FT') {
            time = this.t('event.finished');
        }
        return this.timeLocalisation(time);
    }

    get timeTooltip(): string {
        if (!this.time) {
            return this.t('feed.unavailableTooltip');
        }

        const eventStore = useEventStore();

        return listSiteDataTooltip(this.timeListLocalisation(this.timeList!), eventStore.leagueId!, eventStore.leagueLimitsCategoryId!);
    }

    get scoreTooltip(): string {
        if (!this.score) {
            return this.t('feed.unavailableTooltip');
        }

        const eventStore = useEventStore();

        return listSiteDataTooltip(this.scoreList!, eventStore.leagueId!, eventStore.leagueLimitsCategoryId!);
    }

    protected timeLocalisation(time: string): string {
        if (this.locale === 'en') {
            return time;
        }

        // "1H" -> "1T", "2H" -> "2T", "FT" -> "FIM", "HT" -> "INT"
        let localized = time;
        localized = localized
            .replace(/\b1H\b/g, '1T')
            .replace(/\b2H\b/g, '2T')
            .replace(/\bFT\b/g, 'FIM')
            .replace(/\bHT\b/g, 'INT');
        return localized;
    }

    protected timeListLocalisation(timeList: [number, string, number, number][]): [number, string, number, number][] {
        if (this.locale === 'en') {
            return timeList;
        }

        return timeList.map(([bookmakerId, time, n2, n3]) => [
            bookmakerId,
            time
                .replace(/\b1H\b/g, '1T')
                .replace(/\b2H\b/g, '2T')
                .replace(/\bFT\b/g, 'FIM')
                .replace(/\bHT\b/g, 'INT'),
            n2,
            n3,
        ]);
    }
}
